/**
 * Daily Work Queue Content Component
 *
 * Daily view of production sessions queue.
 * Used as a tab content in the combined Schedule page.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, RefreshCw, Users, Layers, Printer, Download, Plus, X, User, ScanLine, Camera } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import productionService, { DailyWorkQueueItem } from '../../services/productionService';
import workScheduleService, { ScheduledWorkforceResponse } from '../../services/workScheduleService';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { wsService } from '../../services/websocket';
import { QUERY_REFETCH_SLOW } from '../../constants/queryConfig';
import { formatMMDDYYYY } from '../../utils/dateUtils';
import ClockInModal from '../../components/ClockInModal';
import QuickAssignPopover, { AssignType } from '../../components/QuickAssignPopover';
import { getStatusBadgeClass } from '../../utils/statusColors';
import { useTour } from '../../components/TourProvider';

const STATUS_PRIORITY: Record<string, number> = {
  active: 1,
  paused: 2,
  ready: 3,
  planned: 4,
  completed: 5,
  cancelled: 6,
};

type SortColumn = 'status' | 'station_name' | 'operator_name' | 'packer_name' | 'product_name' | 'expected_boxes' | 'boxes_produced' | 'scheduled_date' | 'sub_session_number';


const formatDateForDisplay = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${weekday} - ${mm}/${dd}/${d.getFullYear()}`;
};

// Short date for mobile: "Apr/07"
const formatDateShort = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const dd = String(d.getDate()).padStart(2, '0');
  return `${month}/${dd}`;
};

// Mock session card shown during tour mode when no real data exists
const TOUR_MOCK_SESSION: DailyWorkQueueItem = {
  sub_session_number: 'DEMO-001',
  session_number: 'DEMO-001',
  status: 'ready',
  station_name: 'Station A',
  station_id: 1,
  operator_name: 'John D.',
  operator_employee_number: '0001',
  packer_name: 'Jane S.',
  packer_employee_number: '0002',
  product_name: 'Sample Product',
  product_chinese_name: null,
  product_sku: 'SP001',
  expected_boxes: 10,
  boxes_produced: 0,
  scheduled_date: new Date().toISOString().split('T')[0],
};

const DailyWorkQueueContent: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isTourMode, tourEvent } = useTour();
  const queryClient = useQueryClient();
  const canAssign = user?.role !== 'O' && user?.role !== 'operator';

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const [selectedDate, setSelectedDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('status');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [clockInItem, setClockInItem] = useState<DailyWorkQueueItem | null>(null);
  const [assignTarget, setAssignTarget] = useState<{
    subSessionNumber: string;
    type: AssignType;
    anchorEl: HTMLElement;
    currentAssigneeId?: string;
    subSessionStatus?: string;
  } | null>(null);

  const { data: items = [], isLoading, isFetching } = useQuery(
    ['daily-work-queue', selectedDate],
    () => productionService.getDailyWorkQueue(selectedDate),
    { keepPreviousData: true, staleTime: 30000, refetchInterval: QUERY_REFETCH_SLOW }
  );

  const { data: workforce, isLoading: loadingWorkforce } = useQuery(
    ['scheduled-workforce', selectedDate],
    () => workScheduleService.getScheduledWorkforce(selectedDate),
    { staleTime: 0 }
  );

  // Force refetch workforce data on mount (e.g., after switching from Weekly View)
  useEffect(() => {
    queryClient.invalidateQueries(['scheduled-workforce']);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    wsService.connect();

    const handleUpdate = () => {
      queryClient.invalidateQueries(['daily-work-queue']);
      queryClient.invalidateQueries(['scheduled-workforce']);
    };

    wsService.on('production-session-updated', handleUpdate);
    wsService.on('production-session-created', handleUpdate);
    wsService.on('schedule-updated', handleUpdate);
    wsService.on('station-updated', handleUpdate);
    wsService.on('worker-clocked-in', handleUpdate);
    wsService.on('worker-clocked-out', handleUpdate);
    wsService.on('box-packed', handleUpdate);
    wsService.on('box-cancelled', handleUpdate);
    wsService.on('box-deleted', handleUpdate);

    return () => {
      wsService.off('production-session-updated', handleUpdate);
      wsService.off('production-session-created', handleUpdate);
      wsService.off('schedule-updated', handleUpdate);
      wsService.off('station-updated', handleUpdate);
      wsService.off('worker-clocked-in', handleUpdate);
      wsService.off('worker-clocked-out', handleUpdate);
      wsService.off('box-packed', handleUpdate);
      wsService.off('box-cancelled', handleUpdate);
      wsService.off('box-deleted', handleUpdate);
    };
  }, [queryClient]);

  const filteredAndSorted = useMemo(() => {
    // Always exclude cancelled sessions from the daily queue
    let result = items.filter(item => item.status !== 'cancelled');

    if (statusFilter) {
      result = result.filter(item => item.status === statusFilter);
    }

    result.sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'status') {
        cmp = (STATUS_PRIORITY[a.status] || 99) - (STATUS_PRIORITY[b.status] || 99);
      } else if (sortColumn === 'expected_boxes' || sortColumn === 'boxes_produced') {
        cmp = (a[sortColumn] || 0) - (b[sortColumn] || 0);
      } else if (sortColumn === 'scheduled_date') {
        cmp = (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
      } else {
        cmp = (a[sortColumn] || '').toString().localeCompare((b[sortColumn] || '').toString());
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    // Inject mock session during tour when list is empty
    if (result.length === 0 && isTourMode) {
      return [TOUR_MOCK_SESSION];
    }

    return result;
  }, [items, statusFilter, sortColumn, sortDirection, isTourMode]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const navigateDate = (direction: number) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + direction);
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  const handlePrint = () => {
    if (filteredAndSorted.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const statusLabels: Record<string, string> = {
      planned: 'Submitted',
      ready: 'Ready',
      active: 'Active',
      paused: 'Paused',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };

    const statusStyles: Record<string, string> = {
      planned: 'background-color: #f3e8ff; color: #7c3aed;',
      ready: 'background-color: #cffafe; color: #0891b2;',
      active: 'background-color: #dcfce7; color: #16a34a;',
      paused: 'background-color: #fef3c7; color: #d97706;',
      completed: 'background-color: #dcfce7; color: #15803d;',
      cancelled: 'background-color: #fee2e2; color: #dc2626;',
    };

    const tableRows = filteredAndSorted.map(item => {
      const statusStyle = statusStyles[item.status] || '';
      return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">
            <span style="display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 500; ${statusStyle}">
              ${statusLabels[item.status] || item.status}
            </span>
          </td>
          <td style="border: 1px solid #ddd; padding: 8px;">${item.station_name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${item.operator_name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${item.packer_name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: 500;">${item.product_name}${item.product_chinese_name ? `<br><span style="font-weight: normal; font-size: 11px; color: #666;">${item.product_chinese_name}</span>` : ''}<br><span style="font-weight: normal; font-size: 10px; color: #999; font-family: monospace;">${item.product_sku}</span></td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item.expected_boxes}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center; ${item.boxes_produced > 0 ? 'color: #16a34a; font-weight: 500;' : 'color: #9ca3af;'}">${item.boxes_produced}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${formatMMDDYYYY(item.scheduled_date)}</td>
          <td style="border: 1px solid #ddd; padding: 8px; font-family: monospace; font-size: 11px;">${item.sub_session_number}</td>
        </tr>
      `;
    }).join('');

    let summaryHtml = '';
    if (workforce && (workforce.summary.total_operators > 0 || workforce.summary.total_packers > 0)) {
      summaryHtml = `
        <div class="summary">
          <div class="summary-item"><strong>${t('dailyWorkQueue.operators')}:</strong> ${workforce.summary.available_operators}/${workforce.summary.total_operators}</div>
          <div class="summary-item"><strong>${t('dailyWorkQueue.packers')}:</strong> ${workforce.summary.available_packers}/${workforce.summary.total_packers}</div>
          <div class="summary-item"><strong>${t('dailyWorkQueue.stations')}:</strong> ${workforce.summary.total_stations}</div>
          <div class="summary-item"><strong>Total Sub-Sessions:</strong> ${filteredAndSorted.length}</div>
        </div>
      `;
    }

    const filterInfo = statusFilter
      ? `<div class="filters"><strong>Filter:</strong> ${statusLabels[statusFilter] || statusFilter}</div>`
      : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('dailyWorkQueue.title')} - ${formatDateForDisplay(selectedDate)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .subtitle { font-size: 14px; color: #666; margin-bottom: 10px; }
          .filters { margin-bottom: 10px; font-size: 12px; color: #666; }
          .summary { margin-bottom: 15px; font-size: 14px; display: flex; gap: 30px; }
          .summary-item { padding: 8px 16px; background: #f3f4f6; border-radius: 4px; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; }
          th { background-color: #e5e7eb; color: #111; padding: 10px 8px; text-align: left; border: 1px solid #d1d5db; font-weight: 600; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          @media print {
            body { margin: 0; padding: 10px; }
            h1 { font-size: 14px; }
          }
        </style>
      </head>
      <body>
        <h1>${t('dailyWorkQueue.title')}</h1>
        <div class="subtitle">${formatDateForDisplay(selectedDate)}</div>
        ${filterInfo}
        ${summaryHtml}
        <table>
          <thead>
            <tr>
              <th>${t('dailyWorkQueue.status')}</th>
              <th>${t('dailyWorkQueue.station')}</th>
              <th>${t('dailyWorkQueue.operator')}</th>
              <th>${t('dailyWorkQueue.packer')}</th>
              <th>${t('dailyWorkQueue.product')}</th>
              <th style="text-align: center;">${t('dailyWorkQueue.expectedBoxes')}</th>
              <th style="text-align: center;">${t('dailyWorkQueue.producedBoxes')}</th>
              <th>${t('dailyWorkQueue.scheduledDate')}</th>
              <th>${t('dailyWorkQueue.sessionNumber')}</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleExport = () => {
    if (filteredAndSorted.length === 0) return;

    const statusLabels: Record<string, string> = {
      planned: 'Submitted',
      ready: 'Ready',
      active: 'Active',
      paused: 'Paused',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };

    const headers = [
      t('dailyWorkQueue.status'),
      t('dailyWorkQueue.station'),
      t('dailyWorkQueue.operator'),
      t('dailyWorkQueue.packer'),
      t('dailyWorkQueue.product'),
      t('dailyWorkQueue.expectedBoxes'),
      t('dailyWorkQueue.producedBoxes'),
      t('dailyWorkQueue.scheduledDate'),
      t('dailyWorkQueue.sessionNumber'),
    ];

    const rows = filteredAndSorted.map(item => [
      statusLabels[item.status] || item.status,
      item.station_name || '-',
      item.operator_name || '-',
      item.packer_name || '-',
      `${item.product_name}${item.product_chinese_name ? ` / ${item.product_chinese_name}` : ''} (${item.product_sku})`,
      item.expected_boxes,
      item.boxes_produced,
      formatMMDDYYYY(item.scheduled_date),
      item.sub_session_number,
    ]);

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const headerStyle = {
      fill: { fgColor: { rgb: 'E5E7EB' } },
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        bottom: { style: 'thin', color: { rgb: '9CA3AF' } }
      }
    };

    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = headerStyle;
      }
    });

    ws['!cols'] = [
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Work Queue');

    XLSX.writeFile(wb, `daily-work-queue-${selectedDate}.xlsx`);
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return <ChevronUp className="w-3 h-3 opacity-0 group-hover:opacity-30" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3" />
      : <ChevronDown className="w-3 h-3" />;
  };

  const columns: { key: SortColumn; labelKey: string }[] = [
    { key: 'status', labelKey: 'dailyWorkQueue.status' },
    { key: 'station_name', labelKey: 'dailyWorkQueue.station' },
    { key: 'operator_name', labelKey: 'dailyWorkQueue.operator' },
    { key: 'packer_name', labelKey: 'dailyWorkQueue.packer' },
    { key: 'product_name', labelKey: 'dailyWorkQueue.product' },
    { key: 'expected_boxes', labelKey: 'dailyWorkQueue.expectedBoxes' },
    { key: 'boxes_produced', labelKey: 'dailyWorkQueue.producedBoxes' },
    { key: 'scheduled_date', labelKey: 'dailyWorkQueue.scheduledDate' },
    { key: 'sub_session_number', labelKey: 'dailyWorkQueue.sessionNumber' },
  ];

  return (
    <div className="space-y-6">
      {/* Header with navigation — matches Weekly View layout */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Mobile: compact date nav only */}
        <div className="sm:hidden flex items-center justify-between px-3 py-2">
          {selectedDate !== today ? (
            <button
              onClick={() => setSelectedDate(today)}
              className="px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors border border-indigo-300 dark:border-indigo-600"
            >
              {t('dailyWorkQueue.today')}
            </button>
          ) : (
            <div className="w-10" />
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDate(-1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-base font-semibold text-gray-900 dark:text-white min-w-[70px] text-center">
              {formatDateShort(selectedDate)}
            </span>
            <button
              onClick={() => navigateDate(1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          <button
            onClick={() => {
              queryClient.invalidateQueries(['daily-work-queue']);
              queryClient.invalidateQueries(['scheduled-workforce']);
            }}
            disabled={isFetching}
            className="p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 transition-transform ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Desktop: full nav bar */}
        <div className="hidden sm:grid grid-cols-3 items-center gap-2 px-4 py-3">
          {/* Left: Today button + count */}
          <div className="flex items-center gap-2 justify-self-start">
            {selectedDate !== today && (
              <button
                onClick={() => setSelectedDate(today)}
                className="px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors border border-indigo-300 dark:border-indigo-600"
              >
                {t('dailyWorkQueue.today')}
              </button>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {filteredAndSorted.length} {filteredAndSorted.length === 1 ? 'sub-session' : 'sub-sessions'}
            </span>
          </div>

          {/* Center: Date navigation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => navigateDate(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-lg font-semibold text-gray-900 dark:text-white min-w-[220px] text-center">
              {formatDateForDisplay(selectedDate)}
            </span>
            <button
              onClick={() => navigateDate(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 justify-self-end">
            <button
              onClick={() => {
                queryClient.invalidateQueries(['daily-work-queue']);
                queryClient.invalidateQueries(['scheduled-workforce']);
              }}
              disabled={isFetching}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 transition-transform ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handlePrint}
              disabled={filteredAndSorted.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              title={t('common.print')}
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={handleExport}
              disabled={filteredAndSorted.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              title={t('common.export')}
            >
              <Download className="w-4 h-4" />
              {t('common.export')}
            </button>
          </div>
        </div>
      </div>

      {/* Scheduled Workforce Summary — hidden on mobile */}
      {workforce && (workforce.summary.total_operators > 0 || workforce.summary.total_packers > 0) && (
        <div className="hidden sm:block backdrop-blur-md bg-white/30 dark:bg-gray-900/30 rounded-2xl p-4 border border-white/20 dark:border-gray-700/20">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />
            {t('dailyWorkQueue.scheduledWorkforce')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Operators */}
            <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dailyWorkQueue.operators')}</span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {workforce.summary.available_operators}/{workforce.summary.total_operators}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('dailyWorkQueue.availableOf')}
              </div>
              {workforce.operators.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {workforce.operators.map((op) => (
                    <div
                      key={op.employee_number}
                      className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
                        op.is_assigned && op.assignment_status
                          ? getStatusBadgeClass(op.assignment_status)
                          : 'bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <span className="truncate">{op.full_name || op.employee_number}</span>
                      <span className="ml-2 flex-shrink-0">
                        {op.is_assigned && op.assignment_status ? (
                          <span>{op.assignment_status}</span>
                        ) : op.station_name ? (
                          <span className="text-blue-600 dark:text-blue-400">{op.station_name}</span>
                        ) : (
                          <span className="text-gray-400">{t('dailyWorkQueue.noStation')}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Packers */}
            <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dailyWorkQueue.packers')}</span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {workforce.summary.available_packers}/{workforce.summary.total_packers}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('dailyWorkQueue.availableOf')}
              </div>
              {workforce.packers.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {workforce.packers.map((p) => (
                    <div
                      key={p.employee_number}
                      className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
                        p.is_assigned && p.assignment_status
                          ? getStatusBadgeClass(p.assignment_status)
                          : 'bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <span className="truncate">{p.full_name || p.employee_number}</span>
                      {p.is_assigned && (
                        <span className="ml-2">{p.assignment_status || t('dailyWorkQueue.assigned')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stations */}
            <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  {t('dailyWorkQueue.stations')}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {workforce.summary.total_stations}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('dailyWorkQueue.scheduledStations')}
              </div>
              {workforce.stations.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {workforce.stations.map((s) => (
                    <div
                      key={s.station_id}
                      className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300"
                    >
                      {s.station_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Glassmorphic card list */}
      <div className="backdrop-blur-md bg-white/30 dark:bg-gray-900/30 rounded-2xl p-4 border border-white/20 dark:border-gray-700/20">
        {/* Column headers - desktop only */}
        <div className="hidden md:grid md:grid-cols-9 gap-2 px-4 pb-3 mb-2 border-b border-white/20 dark:border-gray-700/30">
          {columns.map(col => (
            <div
              key={col.key}
              onClick={() => handleSort(col.key)}
              className="group flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              {t(col.labelKey)}
              <SortIcon column={col.key} />
            </div>
          ))}
        </div>

        {/* Cards */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            </div>
          ) : filteredAndSorted.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
              {t('dailyWorkQueue.noData')}
            </div>
          ) : (
            filteredAndSorted.map(item => {
              const isClickable = ['pending', 'submitted', 'received', 'ready', 'active', 'paused'].includes(item.status);
              return (
              <div
                key={item.sub_session_number}
                data-tour="schedule-session-card"
                onClick={() => isClickable && setClockInItem(item)}
                className={`grid grid-cols-2 md:grid-cols-9 gap-2 md:gap-2 items-start md:items-center p-4 md:px-4 md:py-4 backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/30 rounded-xl shadow-sm hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200 ${isClickable ? 'cursor-pointer' : ''}`}
              >
                {/* Status */}
                <div className="order-1 md:order-none">
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs md:text-sm font-medium ${getStatusBadgeClass(item.status)}`}>
                    {t(`status.${item.status}`) || item.status}
                  </span>
                </div>
                {/* Station — clickable for assign or reassign when non-terminal */}
                <div className="order-3 md:order-none text-sm text-gray-900 dark:text-gray-100 truncate">
                  <span className="md:hidden text-gray-500">{t('dailyWorkQueue.station')}: </span>
                  {item.station_name ? (
                    isClickable && canAssign ? (
                      <span
                        onClick={(e) => { e.stopPropagation(); setAssignTarget({ subSessionNumber: item.sub_session_number, type: 'station', anchorEl: e.currentTarget as HTMLElement, currentAssigneeId: item.station_id ? String(item.station_id) : undefined, subSessionStatus: item.status }); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed text-sm cursor-pointer transition-colors ${getStatusBadgeClass(item.status)}`}
                        title={t('dailyWorkQueue.clickToReassign')}
                      >
                        {item.station_name}
                      </span>
                    ) : (
                      item.station_name
                    )
                  ) : isClickable && canAssign ? (
                    <span
                      onClick={(e) => { e.stopPropagation(); setAssignTarget({ subSessionNumber: item.sub_session_number, type: 'station', anchorEl: e.currentTarget as HTMLElement, subSessionStatus: item.status }); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-dashed border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-sm cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                      title={t('dailyWorkQueue.clickToAssign')}
                    >
                      <Plus className="w-3 h-3" />
                      {t('dailyWorkQueue.clickToAssign')}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">—</span>
                  )}
                </div>
                {/* Operator — clickable for assign or reassign when non-terminal */}
                <div className="order-5 md:order-none text-sm text-gray-900 dark:text-gray-100 truncate">
                  <span className="md:hidden text-gray-500">{t('dailyWorkQueue.operator')}: </span>
                  {item.operator_name ? (
                    isClickable && canAssign ? (
                      <span
                        onClick={(e) => { e.stopPropagation(); setAssignTarget({ subSessionNumber: item.sub_session_number, type: 'operator', anchorEl: e.currentTarget as HTMLElement, currentAssigneeId: item.operator_employee_number || undefined }); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed text-xs md:text-sm cursor-pointer transition-colors ${getStatusBadgeClass(item.status)}`}
                        title={t('dailyWorkQueue.clickToReassign')}
                      >
                        {item.operator_name}
                      </span>
                    ) : (
                      item.operator_name
                    )
                  ) : isClickable && canAssign ? (
                    <span
                      onClick={(e) => { e.stopPropagation(); setAssignTarget({ subSessionNumber: item.sub_session_number, type: 'operator', anchorEl: e.currentTarget as HTMLElement }); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-dashed border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-xs md:text-sm cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      {t('dailyWorkQueue.clickToAssign')}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">—</span>
                  )}
                </div>
                {/* Packer — clickable for assign or reassign when non-terminal */}
                <div className="order-7 md:order-none text-sm text-gray-900 dark:text-gray-100 truncate">
                  <span className="md:hidden text-gray-500">{t('dailyWorkQueue.packer')}: </span>
                  {item.packer_name ? (
                    isClickable && canAssign ? (
                      <span
                        onClick={(e) => { e.stopPropagation(); setAssignTarget({ subSessionNumber: item.sub_session_number, type: 'packer', anchorEl: e.currentTarget as HTMLElement, currentAssigneeId: item.packer_employee_number || undefined }); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed text-xs md:text-sm cursor-pointer transition-colors ${getStatusBadgeClass(item.status)}`}
                        title={t('dailyWorkQueue.clickToReassign')}
                      >
                        {item.packer_name}
                      </span>
                    ) : (
                      item.packer_name
                    )
                  ) : isClickable && canAssign ? (
                    <span
                      onClick={(e) => { e.stopPropagation(); setAssignTarget({ subSessionNumber: item.sub_session_number, type: 'packer', anchorEl: e.currentTarget as HTMLElement }); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-dashed border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-xs md:text-sm cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      {t('dailyWorkQueue.clickToAssign')}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">—</span>
                  )}
                </div>
                {/* Product: English name / Chinese name / Item number */}
                <div className="order-2 md:order-none col-span-2 md:col-span-1 min-w-0 mb-2 md:mb-0">
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate">
                    {item.product_name}
                  </div>
                  {item.product_chinese_name && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {item.product_chinese_name}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                    {item.product_sku}
                  </div>
                </div>
                {/* Expected Boxes */}
                <div className="order-6 md:order-none text-sm text-gray-700 dark:text-gray-300 md:text-center">
                  <span className="md:hidden text-gray-500">{t('dailyWorkQueue.expectedBoxes')}: </span>
                  {item.expected_boxes}
                </div>
                {/* Produced Boxes */}
                <div className="order-4 md:order-none text-sm md:text-center">
                  <span className="md:hidden text-gray-500">{t('dailyWorkQueue.producedBoxes')}: </span>
                  <span className={item.boxes_produced > 0 ? 'text-green-700 dark:text-green-400 font-medium' : 'text-gray-400 dark:text-gray-500'}>
                    {item.boxes_produced}
                  </span>
                </div>
                {/* Scheduled Date MM/DD/YYYY */}
                <div className="order-1 md:order-none text-sm text-gray-600 dark:text-gray-400 text-right md:text-left">
                  {formatMMDDYYYY(item.scheduled_date)}
                </div>
                {/* Session # (shows sub-session number) */}
                <div className="order-8 md:order-none text-xs text-gray-500 dark:text-gray-400 font-mono">
                  <span className="md:hidden text-gray-500 text-sm">{t('dailyWorkQueue.sessionNumber')}: </span>
                  {item.sub_session_number}
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Clock-In Modal */}
      <ClockInModal
        isOpen={!!clockInItem}
        onClose={() => setClockInItem(null)}
        item={clockInItem}
        onClockInSuccess={() => queryClient.invalidateQueries(['daily-work-queue'])}
      />

      {/* Tour: Mock Clock-In Modal */}
      {isTourMode && tourEvent === 'clock-in-modal' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="flex min-h-full items-center justify-center p-4">
            <div data-tour="mock-clock-in-modal" className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('clockInModal.title') || 'Clock In to Session'}
                </h2>
                <button className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Sub-session info */}
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300">ready</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Demo Product</span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>Station 1</span>
                  <span className="font-mono">DEMO-001</span>
                  <span>20 expected boxes</span>
                </div>
              </div>

              {/* Current assignments */}
              <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  {t('clockInModal.currentAssignments') || 'Current Assignments'}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('clockInModal.operator') || 'Operator'}</div>
                      <div className="text-sm text-gray-400 italic">{t('clockInModal.notAssigned') || 'Not assigned'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('clockInModal.packer') || 'Packer'}</div>
                      <div className="text-sm text-gray-400 italic">{t('clockInModal.notAssigned') || 'Not assigned'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scan badge area */}
              <div className="px-6 py-5">
                <div className="text-center py-4">
                  <ScanLine className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {t('clockInModal.scanBadge') || 'Scan Employee Badge'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('clockInModal.scanBadgeHint') || 'Scan badge or enter employee number manually'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {t('clockInModal.enterManually')}
                  </span>
                  <input
                    readOnly
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-500"
                    placeholder={t('clockInModal.badgePlaceholder')}
                  />
                  <button className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Go</button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <Camera className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  {t('common.close') || 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick-Assign Popover for unassigned cells */}
      {assignTarget && (
        <QuickAssignPopover
          type={assignTarget.type}
          subSessionNumber={assignTarget.subSessionNumber}
          anchorEl={assignTarget.anchorEl}
          operators={workforce?.operators || []}
          packers={workforce?.packers || []}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            queryClient.invalidateQueries(['daily-work-queue']);
            queryClient.invalidateQueries(['scheduled-workforce']);
          }}
          currentAssigneeId={assignTarget.currentAssigneeId}
          subSessionStatus={assignTarget.subSessionStatus}
        />
      )}
    </div>
  );
};

export default DailyWorkQueueContent;
